import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DictationLoggerService } from '@app/shared/components/dictation-button/services/dictation-logger.service';
import { GoogleSpeechService } from './services/google-speech.service';
import { StreamingSpeechService } from './services/streaming-speech.service';
import { TextStateManager } from './services/text-state-manager.service';
import { Subscription } from 'rxjs';
import { take, filter } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../environments/environment';

type Float32ArrayWithArrayBuffer = Float32Array & { buffer: ArrayBuffer };

@Component({
  selector: 'app-dictation-button',
  templateUrl: './dictation-button.component.html',
  styleUrls: ['./dictation-button.component.scss'],
})
export class DictationButtonComponent implements OnInit, OnDestroy {
  @Input() targetControl?: FormControl;
  @Input() disabled = false;
  @Input() patientId?: string;
  @Input() novaMPSPatientId?: string;
  @Input() practiceTenantId?: string;
  @Input() practiceId?: string;
  @Input() parentEventId?: string; // Optional workflow correlation identifier
  @Input() fieldContext = 'Unknown';
  @Input() componentName = 'DictationButton';
  @Input() formFieldName = 'unknown';
  @Input() useStreaming = true; // Enable Chirp 2 streaming by default
  @Input() processingMode: 'streaming' | 'batch' = 'streaming'; // New: processing mode
  @Input() countdownDurationSeconds = 300; // Maximum length per recording session (5 minutes)

  @Output() textReceived = new EventEmitter<string>();
  @Output() recordingStarted = new EventEmitter<void>();
  @Output() recordingStopped = new EventEmitter<void>();
  @Output() interimTextReceived = new EventEmitter<string>(); // New: for real-time interim results

  isSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  isRecording = false;
  isConnecting = false;
  transcription = '';
  voiceEnergyLevel = 0;
  readonly waveformBarCount = 24;
  waveformLevels: number[] = [];
  countdownRemainingSeconds: number | null = null;
  autoStoppedByTimer = false;
  isProcessingTranscript = false;
  private processedTranscriptTimeout: any = null; // Timeout for processed transcript fallback
  private awaitingProcessedTranscript = false;
  private pendingFinalTranscript = '';
  private hasDeliveredFinalTranscript = false;
  private countdownTimerId?: number;

  private recognition?: any;
  private currentSessionId?: string;
  private lastToggleTime = 0;
  private readonly TOGGLE_DEBOUNCE_MS = 500; // Prevent rapid toggling within 500ms
  // Properties for tracking cursor position
  private cursorPositionOnStart = 0;
  private nativeElement: HTMLTextAreaElement | HTMLInputElement | HTMLElement | null = null;
  private isContentEditableTarget = false;
  private contentEditableSessionActive = false;
  private contentEditableMarker?: HTMLSpanElement | null = null;
  private contentEditableFinalText = '';
  private contentEditableInterimText = '';

  // Speech service properties
  private googleSpeechSubscription?: Subscription;
  private streamingSpeechSubscription?: Subscription;
  private useGoogleSpeech = false;
  private voiceEnergySubscriptions: Subscription[] = [];

  // Voice session context
  private voiceSessionContext: any = null;

  private readonly isProduction = environment.production;

  private browserVoiceEnergyStream?: MediaStream;
  private browserVoiceEnergyContext?: AudioContext;
  private browserVoiceEnergyAnalyser?: AnalyserNode;
  private browserVoiceEnergyData?: Float32ArrayWithArrayBuffer;
  private browserVoiceEnergyAnimationFrame: number | null = null;

  constructor(
    private dictationLogger: DictationLoggerService,
    private googleSpeechService: GoogleSpeechService,
    private streamingSpeechService: StreamingSpeechService,
    private textStateManager: TextStateManager,
    private cdr: ChangeDetectorRef
  ) {
    this.waveformLevels = Array.from({ length: this.waveformBarCount }, () => 12);

    if (!this.isProduction) {
      console.log('🎤 DictationButtonComponent constructor called - component is loading!');
    }

    // Use new streaming service by default if supported
    this.useGoogleSpeech = StreamingSpeechService.isBrowserSupported();
  }

  get canRecord(): boolean {
    return this.isSupported && !this.disabled;
  }

  getButtonTitle(): string {
    if (!this.isSupported) {
      return 'Speech recognition not supported';
    }
    if (this.disabled) {
      return 'Dictation disabled';
    }
    if (this.isConnecting) {
      return 'Connecting...';
    }
    return this.isRecording ? 'Stop recording' : 'Start voice dictation';
  }

  getProcessingModeDescription(): string {
    return this.processingMode === 'batch' ? 'Batch processing (results after recording ends)' : 'Real-time streaming (live results)';
  }

  ngOnInit(): void {
    this.voiceEnergySubscriptions.push(this.streamingSpeechService.voiceEnergy$.subscribe(level => this.handleVoiceEnergy(level)));

    this.voiceEnergySubscriptions.push(this.googleSpeechService.voiceEnergy$.subscribe(level => this.handleVoiceEnergy(level)));
  }

  toggleRecording() {
    // Debounce rapid clicks
    const now = Date.now();
    if (now - this.lastToggleTime < this.TOGGLE_DEBOUNCE_MS) {
      if (!this.isProduction) {
        console.log('⚠️ Toggle debounced - too soon since last toggle');
      }
      return;
    }
    this.lastToggleTime = now;

    // Prevent double-clicks and rapid toggling
    if (this.isRecording) {
      if (!this.isProduction) {
        console.log('🛑 Toggle: Currently recording, stopping...');
      }
      this.stopRecording();
    } else {
      // Prevent starting if already in the process of starting
      if (this.isConnecting || this.isRecording) {
        if (!this.isProduction) {
          console.log('⚠️ Already starting or recording, ignoring toggle');
        }
        return;
      }

      if (!this.isProduction) {
        console.log('🎤 Toggle: Starting recording...');
      }
      // Capture the base text BEFORE starting speech recognition
      // This ensures we get the latest typed content from the DOM
      this.captureCursorPosition();

      // Small delay to ensure DOM and form control are fully synced
      setTimeout(() => {
        this.startRecording();
      }, 100);
    }
  }

  private captureCursorPosition() {
    const possibleSelectors = this.getTargetElementSelectors();

    for (const selector of possibleSelectors) {
      this.nativeElement = document.querySelector(selector) as HTMLTextAreaElement | HTMLInputElement | HTMLElement;
      if (this.nativeElement) {
        if (!this.isProduction) {
          console.log('🎤 Found native element with selector:', selector);
        }
        break;
      }
    }

    if (this.nativeElement) {
      this.isContentEditableTarget = this.isContentEditableElement(this.nativeElement);

      // Force focus to ensure cursor position is accurate
      (this.nativeElement as HTMLElement).focus();

      if (!this.isContentEditableTarget) {
        // Capture current cursor position for native inputs
        const inputElement = this.nativeElement as HTMLTextAreaElement | HTMLInputElement;
        this.cursorPositionOnStart = inputElement.selectionStart || 0;
        if (!this.isProduction) {
          console.log('🎤 Cursor position captured:', this.cursorPositionOnStart);
          console.log('🎤 Total text length:', inputElement.value.length);
          console.log('🎤 Text before cursor:', JSON.stringify(inputElement.value.substring(0, this.cursorPositionOnStart)));
          console.log('🎤 Text after cursor:', JSON.stringify(inputElement.value.substring(this.cursorPositionOnStart)));
        }
      } else {
        if (!this.isProduction) {
          console.log('🎤 Contenteditable target detected');
        }
      }
    } else {
      if (!this.isProduction) {
        console.log('🎤 Could not find target element with any selector');
      }
      this.cursorPositionOnStart = 0;
      this.isContentEditableTarget = false;
    }
  }

  private async startRecording() {
    if (!this.canRecord) return;

    // Session ID will be provided by server via StreamingSpeechService
    // Do not generate client-side sessionId

    // Choose between streaming and batch processing
    if (this.useStreaming && StreamingSpeechService.isBrowserSupported()) {
      if (!this.isProduction) {
        console.log(`🎤 Using streaming speech service in '${this.processingMode}' mode`);
      }
      this.startChirp2CloudRunRecognition();
    } else {
      if (!this.isProduction) {
        console.log('🎤 Using Google Cloud Speech-to-Text batch processing (fallback)');
      }
      this.useGoogleSpeech = true;
      this.startGoogleSpeechRecording();
    }
  }

  private async startChirp2CloudRunRecognition() {
    try {
      if (!this.isProduction) {
        console.log('🎤 Starting streaming speech recognition');
      }
      this.isConnecting = true;
      this.autoStoppedByTimer = false;
      this.isProcessingTranscript = false;
      this.clearCountdownTimer();

      // Reuse provided workflow ID or create one so events remain correlated
      const workflowId = this.parentEventId || uuidv4();

      this.initializeTargetIntegration();
      this.pendingFinalTranscript = '';
      this.hasDeliveredFinalTranscript = false;
      this.awaitingProcessedTranscript = false;

      this.dictationLogger
        .startDictationSession({
          patientId: this.patientId,
          fieldContext: this.fieldContext,
          component: this.componentName,
          formField: this.getFormFieldIdentifier(),
        })
        .subscribe(sessionId => {
          this.currentSessionId = sessionId;
        });

      // Subscribe to transcripts (handles both interim and final segments)
      this.streamingSpeechSubscription = this.streamingSpeechService.transcript$.subscribe({
        next: message => {
          if (!this.isRecording) {
            return; // Ignore messages if recording has stopped
          }

          if (!this.isProduction) {
            console.log('🎤 Streaming transcript:', message.transcript, 'Final:', message.isFinal);
          }

          if (message.isFinal) {
            this.appendPendingFinalTranscriptSegment(message.transcript);
          }
        },
      });

      // Subscribe to the full final transcript sent at the very end of the session
      this.streamingSpeechService.finalTranscript$.subscribe({
        next: message => {
          if (!this.isProduction) {
            console.log('🎤 Final transcript (full session):', message.transcript);
          }
          if (message?.transcript?.trim()) {
            this.pendingFinalTranscript = message.transcript.trim();
            if (!this.isProduction) {
              console.log('🎤 Stored final transcript for deferred display');
            }
          }
        },
      });

      // Subscribe to processed transcripts (LLM output)
      this.streamingSpeechService.processedTranscript$.subscribe({
        next: message => {
          if (!this.isProduction) {
            console.log('🎤 Processed transcript received:', message.processedTranscript);
            console.log('🎤 Raw transcript:', message.rawTranscript);
          }

          if (this.processedTranscriptTimeout) {
            clearTimeout(this.processedTranscriptTimeout);
            this.processedTranscriptTimeout = null;
          }

          if (this.hasDeliveredFinalTranscript) {
            if (!this.isProduction) {
              console.log('⚠️ Processed transcript received after final delivery - ignoring.');
            }
            return;
          }

          this.applyTranscriptToTarget(message.processedTranscript, { isProcessed: true });
        },
      });

      // Subscribe to stream ended message
      this.streamingSpeechService.streamEnded$.subscribe(message => {
        if (!this.isProduction) {
          console.log(
            '🎤 Stream ended message received:',
            message?.reason,
            'Awaiting processed transcript:',
            this.awaitingProcessedTranscript
          );
        }
        if (this.awaitingProcessedTranscript && this.isAnyVoiceSessionActive() && !this.hasDeliveredFinalTranscript) {
          if (!this.isProduction) {
            console.log('🎤 Stream ended - deferring UI update until processed transcript (timeout in 10s)');
          }
          if (this.processedTranscriptTimeout) {
            clearTimeout(this.processedTranscriptTimeout);
          }
          this.processedTranscriptTimeout = setTimeout(() => {
            if (this.awaitingProcessedTranscript && !this.hasDeliveredFinalTranscript) {
              if (this.pendingFinalTranscript) {
                if (!this.isProduction) {
                  console.log('⚠️ Processed transcript timeout - applying raw transcript fallback');
                }
                this.applyTranscriptToTarget(this.pendingFinalTranscript, {
                  isProcessed: false,
                  reason: 'timeout',
                });
              } else {
                if (!this.isProduction) {
                  console.log('⚠️ Processed transcript timeout - ending voice session without transcript');
                }
                this.forceEndVoiceSession();
              }
            }
            this.processedTranscriptTimeout = null;
          }, 10000);
        } else if (!this.isProduction) {
          console.log('🎤 Stream ended message ignored - no pending transcript delivery');
        }
      });

      // Subscribe to errors
      this.streamingSpeechService.error$.subscribe({
        next: error => {
          console.error('🎤 Streaming error received:', error);
          if (!error.recoverable || error.severity === 'critical') {
            this.stopRecording();
            alert('Dictation error: ' + error.error);
          }
        },
      });

      // Start session with LLM processing enabled
      await this.streamingSpeechService.startSession({
        processingMode: this.processingMode,
        patientId: this.novaMPSPatientId,
        patientUuid: this.patientId,
        practiceTenantId: this.practiceTenantId,
        practiceId: this.practiceId,
        fieldContext: this.fieldContext,
        componentName: this.componentName,
        sampleRate: 16000,
        parentEventId: workflowId,
        llmConfig: {
          enabled: true,
          model: 'gemini-2.5-flash',
          prompt:
            'This is a medical doctor dictating a note into a patient record. Using your role as a world-class medical scribe clean up and format the text to reflect medical terminology while keeping the main ideas in the same order as dictated by the doctor. Feel free to correct transcribing errors by substituting the correct medical terms where there is a high probability a mistake has been made. Be fast and very brief, use medical shorthand, do not use any formatting, speed is of the essence',
          promptName: 'medical-scribe-format',
          temperature: 0.7,
          maxOutputTokens: 2048,
        },
      });

      this.isRecording = true;
      this.awaitingProcessedTranscript = true;
      this.isConnecting = false;
      this.recordingStarted.emit();
      this.startCountdownTimer();
      if (!this.isProduction) {
        console.log('✅ Recording session started successfully');
      }
    } catch (error) {
      console.error('Failed to start streaming recognition:', error);
      this.isRecording = false;
      this.isConnecting = false;
      this.awaitingProcessedTranscript = false;
      this.pendingFinalTranscript = '';
      this.hasDeliveredFinalTranscript = false;
      this.startGoogleSpeechRecording();
    }
  }

  private async startGoogleSpeechRecording() {
    try {
      if (!this.isProduction) {
        console.log('🎤 Starting Google Cloud Speech-to-Text recording');
      }

      this.awaitingProcessedTranscript = false;
      this.pendingFinalTranscript = '';
      this.hasDeliveredFinalTranscript = false;
      this.isProcessingTranscript = false;
      this.autoStoppedByTimer = false;
      this.clearCountdownTimer();

      const sessionOptions = {
        patientId: this.patientId,
        contextInfo: {
          fieldContext: this.fieldContext,
          componentName: this.componentName,
          formFieldName: this.getFormFieldIdentifier(),
        },
        sampleRate: 16000,
      };

      // Start logging session
      this.dictationLogger
        .startDictationSession({
          patientId: this.patientId,
          fieldContext: this.fieldContext,
          component: this.componentName,
          formField: this.getFormFieldIdentifier(),
        })
        .subscribe(sessionId => {
          this.currentSessionId = sessionId;
        });

      this.isRecording = true;
      this.transcription = '';
      this.recordingStarted.emit();
      this.startCountdownTimer();

      // Start Google Speech streaming
      const speechStream = await this.googleSpeechService.startStreamingRecognition(this.currentSessionId!, sessionOptions);

      if (!this.isProduction) {
        console.log('🎤 DictationButtonComponent: Subscribing to speechStream:', speechStream);
      }
      this.googleSpeechSubscription = speechStream.subscribe({
        next: result => {
          if (!this.isProduction) {
            console.log('🎤 Google Speech result:', result);
            console.log('🎤 isFinal:', result.isFinal, 'targetControl:', !!this.targetControl, 'nativeElement:', !!this.nativeElement);
          }
          this.transcription = result.transcript;

          if (result.isFinal && this.targetControl && this.nativeElement) {
            if (!this.isProduction) {
              console.log('🎤 Inserting transcript at cursor:', result.transcript);
            }
            this.insertFinalTranscriptAtCursor(result.transcript);
            this.textReceived.emit(result.transcript);
          } else {
            if (!this.isProduction) {
              console.log('🎤 NOT inserting transcript - conditions not met');
            }
          }
        },
        complete: () => {
          if (!this.isProduction) {
            console.log('🎤 GoogleSpeech Observable completed - cleaning up subscription');
          }
          this.googleSpeechSubscription = undefined;
        },
        error: error => {
          console.error('🎤 Google Speech error, falling back to browser API:', error);
          this.googleSpeechSubscription = undefined;
          this.useGoogleSpeech = false;
          this.startBrowserSpeechRecording();
        },
      });
    } catch (error) {
      console.error('🎤 Failed to start Google Speech, falling back to browser API:', error);
      this.useGoogleSpeech = false;
      this.startBrowserSpeechRecording();
    }
  }

  private startBrowserSpeechRecording() {
    try {
      // Try the standard API first, then webkit
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();

      this.awaitingProcessedTranscript = false;
      this.pendingFinalTranscript = '';
      this.hasDeliveredFinalTranscript = false;

      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';

      this.recognition.onstart = () => {
        this.autoStoppedByTimer = false;
        this.isProcessingTranscript = false;
        this.clearCountdownTimer();
        this.isRecording = true;
        this.transcription = '';
        this.recordingStarted.emit();
        this.startBrowserVoiceEnergyMonitor();
        this.startCountdownTimer();

        if (!this.isProduction) {
          console.log('🎤 Recording started. Cursor position:', this.cursorPositionOnStart);
        }

        // Start logging session
        this.dictationLogger
          .startDictationSession({
            patientId: this.patientId,
            fieldContext: this.fieldContext,
            component: this.componentName,
            formField: this.getFormFieldIdentifier(),
          })
          .subscribe(sessionId => {
            this.currentSessionId = sessionId;
          });
      };

      this.recognition.onresult = (event: any) => {
        // CURSOR-BASED APPROACH: Insert dictated text at cursor position
        // Build only the new transcript from the current result event
        let newTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            newTranscript += event.results[i][0].transcript;
          }
        }

        // Also show interim results for UX
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (!event.results[i].isFinal) {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        // Update the displayed transcription
        let completeTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            completeTranscript += event.results[i][0].transcript;
          }
        }
        this.transcription = completeTranscript + interimTranscript;

        // Only process if we have NEW final transcript
        if (this.targetControl && this.nativeElement && newTranscript) {
          this.insertFinalTranscriptAtCursor(newTranscript);
          this.textReceived.emit(newTranscript);
        }
      };

      this.recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        this.isRecording = false;
        this.stopBrowserVoiceEnergyMonitor();

        // Log error
        if (this.currentSessionId) {
          this.dictationLogger
            .endDictationSession(this.currentSessionId, {
              success: false,
              errorMessage: event.error,
            })
            .subscribe();
          this.currentSessionId = undefined;
        }

        // Handle specific error cases
        if (event.error === 'not-allowed') {
          alert('Microphone access denied. Please allow microphone access and try again.');
        } else if (event.error === 'no-speech') {
          if (!this.isProduction) {
            console.log('No speech detected. Try again.');
          }
        }
      };

      this.recognition.onend = () => {
        this.isRecording = false;
        this.recordingStopped.emit();
        this.stopBrowserVoiceEnergyMonitor();

        // Reset tracking variables
        this.cursorPositionOnStart = 0;
        this.nativeElement = null;

        // End logging session
        if (this.currentSessionId) {
          this.dictationLogger
            .endDictationSession(this.currentSessionId, {
              success: true,
              transcriptionLength: this.transcription.length,
            })
            .subscribe();
          this.currentSessionId = undefined;
        }
      };

      this.recognition.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      this.isRecording = false;
    }
  }

  public stopRecording() {
    if (!this.isRecording && !this.awaitingProcessedTranscript && !this.isConnecting) {
      // Already stopped and nothing pending
      return;
    }

    if (!this.isProduction) {
      console.log('🛑 stopRecording() called');
    }
    // Stop streaming speech service if using streaming
    if (this.useStreaming && this.streamingSpeechSubscription) {
      this.streamingSpeechService.stopSession().catch(error => {
        console.error('Error stopping streaming session:', error);
      });

      if (this.streamingSpeechSubscription) {
        this.streamingSpeechSubscription.unsubscribe();
        this.streamingSpeechSubscription = undefined;
      }

      if (!this.isProduction) {
        console.log('🎤 DictationButtonComponent: Stopped streaming recording');
      }
    } else if (this.useGoogleSpeech) {
      // Stop Google Speech service (fallback)
      this.googleSpeechService.stopStreamingRecognition();

      if (this.googleSpeechSubscription) {
        this.googleSpeechSubscription.unsubscribe();
        this.googleSpeechSubscription = undefined;
      }

      if (!this.isProduction) {
        console.log('🎤 DictationButtonComponent: Stopped Google Speech recording');
      }
    } else if (this.recognition) {
      // Stop browser speech recognition
      this.recognition.stop();
      if (!this.isProduction) {
        console.log('🎤 DictationButtonComponent: Stopped browser speech recognition');
      }
    }

    // DO NOT end voice session here - keep it active until processed transcript arrives
    // The voice session will be ended when processed transcript is received or stream ends
    // This preserves the originalSessionStartState needed for text replacement
    // this.textStateManager.endVoiceSession(); // Commented out - will end when processed transcript arrives
    // this.voiceSessionContext = null; // Commented out - will clear when processed transcript arrives

    this.isRecording = false;
    this.isConnecting = false;
    this.clearCountdownTimer();
    this.isProcessingTranscript = this.awaitingProcessedTranscript && !this.hasDeliveredFinalTranscript;
    // Clear processed transcript timeout if it exists
    if (this.processedTranscriptTimeout) {
      clearTimeout(this.processedTranscriptTimeout);
      this.processedTranscriptTimeout = null;
    }

    this.recordingStopped.emit();
    this.stopBrowserVoiceEnergyMonitor();
  }

  private initializeTargetIntegration() {
    if (!this.nativeElement) {
      console.warn('🎤 Native element not found');
      return;
    }

    this.isContentEditableTarget = this.isContentEditableElement(this.nativeElement);

    if (this.isContentEditableTarget) {
      this.startContentEditableVoiceSession();
      return;
    }

    if (!this.targetControl) {
      console.warn('🎤 Target control not provided for dictation');
      return;
    }

    const inputElement = this.nativeElement as HTMLTextAreaElement | HTMLInputElement;

    // Initialize the TextStateManager with current elements
    this.textStateManager.initialize(inputElement, this.targetControl);

    // Start a new voice session
    this.voiceSessionContext = this.textStateManager.startVoiceSession();

    if (!this.isProduction) {
      console.log('🎤 Text state manager initialized and voice session started:', {
        sessionId: this.voiceSessionContext.sessionId,
        startCursor: this.voiceSessionContext.startState.cursorPosition,
        startTextLength: this.voiceSessionContext.startState.text.length,
      });
    }
  }

  private getTargetElementSelectors(): string[] {
    const selectors: string[] = [];
    const trimmedField = this.formFieldName?.trim();
    const hasCustomSelector =
      trimmedField &&
      (trimmedField.startsWith('#') || trimmedField.startsWith('.') || trimmedField.includes('[') || trimmedField.includes(' '));

    if (trimmedField && trimmedField !== 'unknown') {
      if (hasCustomSelector) {
        selectors.push(trimmedField);
      } else {
        selectors.push(`textarea[formcontrolname="${trimmedField}"]`);
        selectors.push(`input[formcontrolname="${trimmedField}"]`);
      }
    }

    selectors.push(
      // No clinicalNote fallback: it is a TinyMCE iframe that querySelector cannot reach.
      'textarea[formcontrolname="patientFileNote"]',
      'textarea',
      'input[type="text"]'
    );

    return selectors;
  }

  private isContentEditableElement(element: Element | null): element is HTMLElement {
    return !!element && element instanceof HTMLElement && element.isContentEditable;
  }

  private getFormFieldIdentifier(): string {
    if (this.formFieldName && this.formFieldName.trim() && this.formFieldName !== 'unknown') {
      return this.formFieldName.trim();
    }
    return 'unspecified';
  }

  private startContentEditableVoiceSession(): void {
    if (!this.nativeElement || !this.isContentEditableElement(this.nativeElement)) {
      console.warn('🎤 Contenteditable target not available');
      return;
    }

    this.cleanupContentEditableMarker();

    const range = this.getActiveRangeWithinElement(this.nativeElement);
    const marker = document.createElement('span');
    marker.dataset.dictationMarker = 'true';
    marker.style.whiteSpace = 'pre-wrap';
    marker.style.display = 'inline';
    marker.id = `dictation-marker-${uuidv4()}`;

    range.deleteContents();
    range.insertNode(marker);

    this.contentEditableMarker = marker;
    this.contentEditableFinalText = '';
    this.contentEditableInterimText = '';
    this.contentEditableSessionActive = true;

    this.focusContentEditableMarker();

    if (!this.isProduction) {
      console.log('🎤 Contenteditable voice session initialized with marker:', marker.id);
    }
  }

  private handleContentEditableInterimText(transcript: string): void {
    if (!this.contentEditableSessionActive) {
      return;
    }
    this.contentEditableInterimText = transcript;
    this.renderContentEditableText();
  }

  private handleContentEditableFinalText(transcript: string): void {
    if (!this.contentEditableSessionActive) {
      return;
    }
    const trimmed = transcript.trim();
    if (trimmed) {
      if (this.contentEditableFinalText) {
        this.contentEditableFinalText += ' ' + trimmed;
      } else {
        this.contentEditableFinalText = trimmed;
      }
    }
    this.contentEditableInterimText = '';
    this.renderContentEditableText();
  }

  private replaceContentEditableWithFullTranscript(transcript: string): void {
    if (!this.contentEditableSessionActive) {
      return;
    }
    this.contentEditableFinalText = transcript.trim();
    this.contentEditableInterimText = '';
    this.renderContentEditableText();
  }

  private renderContentEditableText(): void {
    if (!this.contentEditableMarker) {
      return;
    }

    let assembledText = this.contentEditableFinalText;
    if (this.contentEditableInterimText) {
      assembledText = assembledText ? `${assembledText} ${this.contentEditableInterimText}` : this.contentEditableInterimText;
    }

    const spacedText = this.applyContentEditableSpacing(assembledText);
    this.contentEditableMarker.textContent = spacedText;
    this.transcription = assembledText;

    this.focusContentEditableMarker();
  }

  private applyContentEditableSpacing(text: string): string {
    if (!text) {
      return '';
    }
    const { beforeChar, afterChar } = this.getContentEditableContext();
    const needsSpaceBefore = beforeChar && !beforeChar.match(/\s/) && !text.startsWith(' ');
    const needsSpaceAfter = afterChar && !afterChar.match(/\s/) && !text.endsWith(' ');

    return `${needsSpaceBefore ? ' ' : ''}${text}${needsSpaceAfter ? ' ' : ''}`;
  }

  private getContentEditableContext(): { beforeChar: string; afterChar: string } {
    if (!this.nativeElement || !this.contentEditableMarker || !this.isContentEditableElement(this.nativeElement)) {
      return { beforeChar: '', afterChar: '' };
    }

    const element = this.nativeElement;

    const beforeRange = document.createRange();
    beforeRange.selectNodeContents(element);
    beforeRange.setEndBefore(this.contentEditableMarker);
    const beforeText = beforeRange.toString();

    const afterRange = document.createRange();
    afterRange.selectNodeContents(element);
    afterRange.setStartAfter(this.contentEditableMarker);
    const afterText = afterRange.toString();

    return {
      beforeChar: beforeText.slice(-1),
      afterChar: afterText.slice(0, 1),
    };
  }

  private focusContentEditableMarker(): void {
    if (!this.contentEditableMarker) {
      return;
    }
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(this.contentEditableMarker);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private finalizeContentEditableSession(): void {
    if (!this.contentEditableMarker) {
      return;
    }

    const parent = this.contentEditableMarker.parentNode;
    const textContent = this.contentEditableMarker.textContent || '';

    if (parent) {
      if (textContent) {
        const textNode = document.createTextNode(textContent);
        parent.replaceChild(textNode, this.contentEditableMarker);
        this.placeCaretAfterNode(textNode);
      } else {
        parent.removeChild(this.contentEditableMarker);
      }
    }

    this.contentEditableMarker = null;
    this.contentEditableSessionActive = false;
    this.contentEditableFinalText = '';
    this.contentEditableInterimText = '';
  }

  private endContentEditableVoiceSession(): void {
    if (!this.contentEditableSessionActive) {
      return;
    }
    this.finalizeContentEditableSession();
  }

  private placeCaretAfterNode(node: Node): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private cleanupContentEditableMarker(): void {
    if (this.contentEditableMarker && this.contentEditableMarker.parentNode) {
      this.contentEditableMarker.parentNode.removeChild(this.contentEditableMarker);
    }
    this.contentEditableMarker = null;
    this.contentEditableSessionActive = false;
    this.contentEditableFinalText = '';
    this.contentEditableInterimText = '';
  }

  private getActiveRangeWithinElement(element: HTMLElement): Range {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const currentRange = selection.getRangeAt(0);
      if (element.contains(currentRange.startContainer)) {
        return currentRange.cloneRange();
      }
    }

    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    return range;
  }

  private insertContentEditableTranscript(newTranscript: string): void {
    if (!this.nativeElement || !this.isContentEditableElement(this.nativeElement)) {
      return;
    }

    const element = this.nativeElement;
    const range = this.getActiveRangeWithinElement(element);
    const context = this.getContentEditableContextForRange(element, range);

    const trimmed = newTranscript.trim();
    if (!trimmed) {
      return;
    }

    const needsSpaceBefore = context.beforeChar && !context.beforeChar.match(/\s/) && !trimmed.startsWith(' ');
    const needsSpaceAfter = context.afterChar && !context.afterChar.match(/\s/) && !trimmed.endsWith(' ');
    const dictatedText = `${needsSpaceBefore ? ' ' : ''}${trimmed}${needsSpaceAfter ? ' ' : ''}`;

    range.deleteContents();
    const textNode = document.createTextNode(dictatedText);
    range.insertNode(textNode);
    this.placeCaretAfterNode(textNode);
  }

  private getContentEditableContextForRange(element: HTMLElement, range: Range): { beforeChar: string; afterChar: string } {
    const beforeRange = document.createRange();
    beforeRange.selectNodeContents(element);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    const beforeText = beforeRange.toString();

    const afterRange = document.createRange();
    afterRange.selectNodeContents(element);
    afterRange.setStart(range.endContainer, range.endOffset);
    const afterText = afterRange.toString();

    return {
      beforeChar: beforeText.slice(-1),
      afterChar: afterText.slice(0, 1),
    };
  }

  private appendPendingFinalTranscriptSegment(transcript: string | undefined | null): void {
    const trimmed = transcript?.trim();
    if (!trimmed) {
      return;
    }

    this.pendingFinalTranscript = this.pendingFinalTranscript ? `${this.pendingFinalTranscript} ${trimmed}` : trimmed;
  }

  private applyTranscriptToTarget(
    transcript: string | undefined | null,
    options: { isProcessed: boolean; reason?: string } = { isProcessed: true }
  ): void {
    const cleaned = transcript?.trim();
    if (!cleaned) {
      if (!this.isProduction) {
        console.warn('⚠️ applyTranscriptToTarget called with empty transcript');
      }
      this.pendingFinalTranscript = '';
      this.awaitingProcessedTranscript = false;
      return;
    }

    const resolvedTranscript = transcript ?? cleaned;
    const label = options.isProcessed ? 'processed' : 'raw fallback';
    const reason = options.reason ? ` (${options.reason})` : '';

    if (!this.isProduction) {
      console.log(`🎤 Applying ${label} transcript${reason}:`, cleaned);
    }

    let applied = false;

    if (this.isContentEditableTarget && this.contentEditableSessionActive) {
      this.replaceContentEditableWithFullTranscript(resolvedTranscript);
      this.transcription = resolvedTranscript;
      this.textReceived.emit(resolvedTranscript);
      this.cdr.detectChanges();
      this.endContentEditableVoiceSession();
      applied = true;
    } else if (this.textStateManager.isVoiceSessionActive()) {
      this.textStateManager.replaceWithFullTranscript(resolvedTranscript);
      this.transcription = this.textStateManager.getDisplayText();
      this.textReceived.emit(resolvedTranscript);
      this.cdr.detectChanges();
      this.textStateManager.endVoiceSession();
      this.voiceSessionContext = null;
      applied = true;
    } else if (this.targetControl && this.nativeElement) {
      this.insertFinalTranscriptAtCursor(resolvedTranscript);
      this.textReceived.emit(resolvedTranscript);
      applied = true;
    } else if (!this.isProduction) {
      console.warn('⚠️ No active voice session context available for transcript application.');
    }

    this.pendingFinalTranscript = '';
    this.awaitingProcessedTranscript = false;
    this.isProcessingTranscript = false;

    if (applied) {
      this.hasDeliveredFinalTranscript = true;
    }

    if (this.processedTranscriptTimeout) {
      clearTimeout(this.processedTranscriptTimeout);
      this.processedTranscriptTimeout = null;
    }
  }

  private forceEndVoiceSession(): void {
    if (this.isContentEditableTarget) {
      this.endContentEditableVoiceSession();
    } else if (this.textStateManager.isVoiceSessionActive()) {
      this.textStateManager.endVoiceSession();
      this.voiceSessionContext = null;
    }

    this.awaitingProcessedTranscript = false;
    this.pendingFinalTranscript = '';
    this.isProcessingTranscript = false;
    if (this.processedTranscriptTimeout) {
      clearTimeout(this.processedTranscriptTimeout);
      this.processedTranscriptTimeout = null;
    }
  }

  trackByWaveformBar(index: number): number {
    return index;
  }

  private handleVoiceEnergy(level: number): void {
    const normalized = Math.min(1, Math.max(0, level));
    const targetLevel = this.isRecording ? normalized : 0;
    const smoothed = this.voiceEnergyLevel * 0.5 + targetLevel * 0.5;
    this.voiceEnergyLevel = Number(smoothed.toFixed(4));
    this.updateWaveformLevels(this.voiceEnergyLevel);
  }

  private updateWaveformLevels(level: number): void {
    if (!this.waveformLevels.length) {
      this.waveformLevels = Array.from({ length: this.waveformBarCount }, () => 12);
    }

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const idleBaseline = 14;
    const activeLevel = Math.max(0, level - 0.02);
    const isIdle = activeLevel <= 0.005;

    const updated = this.waveformLevels.map((current, index) => {
      const target = isIdle ? idleBaseline : this.calculateWaveformTarget(activeLevel, now, index);
      const eased = current + (target - current) * (isIdle ? 0.25 : 0.45);
      return Math.max(4, Math.min(100, eased));
    });

    this.waveformLevels = updated;
    this.cdr.markForCheck();
  }

  private calculateWaveformTarget(level: number, now: number, index: number): number {
    const phase = (index / this.waveformBarCount) * Math.PI;
    const variance = (Math.sin(now / 140 + phase) + 1) / 2; // 0-1
    const centerOffset = Math.abs(index - (this.waveformBarCount - 1) / 2);
    const base = 18 + centerOffset * 2;
    return base + level * 95 * (0.35 + variance * 0.65);
  }

  private calculateVoiceEnergyFromBuffer(buffer: Float32Array): number {
    if (!buffer.length) {
      return 0;
    }

    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      const sample = buffer[i];
      sumSquares += sample * sample;
    }

    const rms = Math.sqrt(sumSquares / buffer.length);
    const threshold = 0.003;
    const normalized = Math.min(1, Math.max(0, (rms - threshold) * 12));
    return Number(normalized.toFixed(4));
  }

  private async startBrowserVoiceEnergyMonitor(): Promise<void> {
    if (
      this.browserVoiceEnergyStream ||
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      return;
    }

    try {
      this.browserVoiceEnergyStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      if (typeof window === 'undefined') {
        this.stopBrowserVoiceEnergyMonitor();
        return;
      }

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        this.stopBrowserVoiceEnergyMonitor();
        return;
      }

      this.browserVoiceEnergyContext = new AudioContextClass();
      const source = this.browserVoiceEnergyContext.createMediaStreamSource(this.browserVoiceEnergyStream);
      this.browserVoiceEnergyAnalyser = this.browserVoiceEnergyContext.createAnalyser();
      this.browserVoiceEnergyAnalyser.fftSize = 256;
      source.connect(this.browserVoiceEnergyAnalyser);
      const buffer = new ArrayBuffer(this.browserVoiceEnergyAnalyser.fftSize * Float32Array.BYTES_PER_ELEMENT);
      this.browserVoiceEnergyData = new Float32Array(buffer) as Float32ArrayWithArrayBuffer;

      const updateEnergy = () => {
        if (!this.browserVoiceEnergyAnalyser || !this.browserVoiceEnergyData) {
          return;
        }

        const energyView = this.browserVoiceEnergyData as any;
        this.browserVoiceEnergyAnalyser.getFloatTimeDomainData(energyView);
        const level = this.calculateVoiceEnergyFromBuffer(energyView as Float32Array);
        this.handleVoiceEnergy(level);

        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          this.browserVoiceEnergyAnimationFrame = window.requestAnimationFrame(updateEnergy);
        }
      };

      updateEnergy();
    } catch (error) {
      if (!this.isProduction) {
        console.warn('🎤 Unable to start browser voice energy monitor:', error);
      }
    }
  }

  private stopBrowserVoiceEnergyMonitor(): void {
    if (this.browserVoiceEnergyAnimationFrame && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.browserVoiceEnergyAnimationFrame);
    }
    this.browserVoiceEnergyAnimationFrame = null;

    if (this.browserVoiceEnergyAnalyser) {
      this.browserVoiceEnergyAnalyser.disconnect();
      this.browserVoiceEnergyAnalyser = undefined;
    }

    if (this.browserVoiceEnergyContext) {
      this.browserVoiceEnergyContext.close().catch(() => {});
      this.browserVoiceEnergyContext = undefined;
    }

    if (this.browserVoiceEnergyStream) {
      this.browserVoiceEnergyStream.getTracks().forEach(track => track.stop());
      this.browserVoiceEnergyStream = undefined;
    }

    this.browserVoiceEnergyData = undefined;
    this.handleVoiceEnergy(0);
  }

  private isAnyVoiceSessionActive(): boolean {
    return this.isContentEditableTarget ? this.contentEditableSessionActive : this.textStateManager.isVoiceSessionActive();
  }

  // Text integration is handled by TextStateManager service.
  // It accumulates final segments and displays interim text.

  /**
   * Legacy method - kept for compatibility with Google Speech fallback
   * Uses simple insertion without robust state management
   */
  private insertFinalTranscriptAtCursor(newTranscript: string): void {
    if (!this.nativeElement) {
      return;
    }

    if (this.isContentEditableElement(this.nativeElement)) {
      this.insertContentEditableTranscript(newTranscript);
      return;
    }

    if (!this.targetControl) {
      return;
    }

    // Simple insertion at current cursor position
    const inputElement = this.nativeElement as HTMLTextAreaElement | HTMLInputElement;
    const currentText = inputElement.value;
    const currentCursorPos = inputElement.selectionStart || 0;
    const textBeforeCursor = currentText.substring(0, currentCursorPos);
    const textAfterCursor = currentText.substring(currentCursorPos);

    // Add appropriate spacing
    const needsSpaceBefore = textBeforeCursor.length > 0 && !textBeforeCursor.endsWith(' ') && !newTranscript.startsWith(' ');
    const needsSpaceAfter = !newTranscript.match(/[.!?,:;\s]$/);
    const dictatedText = (needsSpaceBefore ? ' ' : '') + newTranscript.trim() + (needsSpaceAfter ? ' ' : '');

    const newValue = textBeforeCursor + dictatedText + textAfterCursor;
    const newCursorPosition = currentCursorPos + dictatedText.length;

    // Update UI
    inputElement.value = newValue;
    this.targetControl.setValue(newValue);
    this.targetControl.markAsDirty();
    this.targetControl.markAsTouched();
    inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    this.cdr.detectChanges();

    if (!this.isProduction) {
      console.log('🎤 Legacy text insertion completed:', {
        transcript: newTranscript,
        newLength: newValue.length,
        newCursorPos: newCursorPosition,
      });
    }
  }

  ngOnDestroy() {
    this.voiceEnergySubscriptions.forEach(subscription => subscription.unsubscribe());
    this.voiceEnergySubscriptions = [];
    this.stopBrowserVoiceEnergyMonitor();
    this.clearCountdownTimer();

    // Clear processed transcript timeout
    if (this.processedTranscriptTimeout) {
      clearTimeout(this.processedTranscriptTimeout);
      this.processedTranscriptTimeout = null;
    }

    // Clean up Google Speech subscription
    if (this.googleSpeechSubscription) {
      this.googleSpeechSubscription.unsubscribe();
    }

    // Clean up streaming speech subscription
    if (this.streamingSpeechSubscription) {
      this.streamingSpeechSubscription.unsubscribe();
    }

    // Stop streaming speech service
    this.streamingSpeechService.stopSession().catch(() => {
      // Ignore errors during cleanup
    });

    // Stop Google Speech service
    if (this.useGoogleSpeech) {
      this.googleSpeechService.stopStreamingRecognition();
    }

    // Clean up browser speech recognition
    if (this.recognition) {
      this.recognition.stop();
    }

    // Cleanup text state manager
    this.textStateManager.cleanup();

    if (this.contentEditableSessionActive) {
      this.finalizeContentEditableSession();
    }

    // Reset tracking variables
    this.cursorPositionOnStart = 0;
    this.nativeElement = null;
  }

  private startCountdownTimer(): void {
    const normalizedDuration = Math.max(10, Math.floor(this.countdownDurationSeconds || 0));
    if (!normalizedDuration || Number.isNaN(normalizedDuration)) {
      return;
    }

    this.clearCountdownTimer();
    this.countdownRemainingSeconds = normalizedDuration;

    this.countdownTimerId = window.setInterval(() => {
      if (this.countdownRemainingSeconds === null) {
        return;
      }

      this.countdownRemainingSeconds = Math.max(0, this.countdownRemainingSeconds - 1);
      this.cdr.markForCheck();

      if (this.countdownRemainingSeconds <= 0) {
        this.handleCountdownExpired();
      }
    }, 1000);
  }

  private handleCountdownExpired(): void {
    this.clearCountdownTimer();
    if (this.isRecording) {
      this.autoStoppedByTimer = true;
      this.stopRecording();
      this.cdr.detectChanges();
    }
  }

  private clearCountdownTimer(): void {
    if (this.countdownTimerId) {
      clearInterval(this.countdownTimerId);
      this.countdownTimerId = undefined;
    }
    this.countdownRemainingSeconds = null;
  }

  getCountdownDisplay(): string {
    if (this.countdownRemainingSeconds === null || this.countdownRemainingSeconds === undefined) {
      return '';
    }
    const minutes = Math.floor(this.countdownRemainingSeconds / 60);
    const seconds = this.countdownRemainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
